import type { GasViscosityCalculator } from "../d";

const getPositiveFiniteOrFallback = (value: number, fallback: number) => {
  return Number.isFinite(value) && value > 0 ? value : fallback;
};

const coef = {
  0: -2.462182,
  1: 2.97054714,
  2: -0.286264054,
  3: 0.00805420522,
  4: 2.8086949,
  5: -3.49803305,
  6: 0.36037302,
  7: -0.0104432413,
  8: -0.793385684,
  9: 1.39643306,
  10: -0.1499144925,
  11: 0.00441015512,
  12: 0.0839387178,
  13: -0.186408848,
  14: 0.0203367881,
  15: -0.000609579263
};

export const standingsDempseyGasViscosityCalculator: GasViscosityCalculator =
  params => {
    const {
      temperatureF,
      gasSpecificGravity,
      psuedoCriticals: { psuedoReducedPressure, psuedoReducedTemperature },
      gasPercentages: {
        n2NitrogenPercent,
        cO2CarbonDioxidePercent,
        h2SHydrogenSulfidePercent
      }
    } = params;
    const safeGasSpecificGravity = getPositiveFiniteOrFallback(
      gasSpecificGravity,
      1
    );

    // N61: =(1.709*(10^-5)-2.062*(10^-6)*D32)*D4+8.188*(10^-3)-6.15*(10^-3)*LOG10(D32)
    const u1Uncorrected =
      (1.709 * 10 ** -5 - 2.062 * 10 ** -6 * safeGasSpecificGravity) *
        temperatureF +
      8.188 * 10 ** -3 -
      6.15 * 10 ** -3 * Math.log(safeGasSpecificGravity);

    // N63: =D20*((8.48*10^-3)*LOG10(D32)+9.59*10^-3)
    const n2Correction =
      n2NitrogenPercent *
      (8.48e-3 * Math.log10(safeGasSpecificGravity) + 9.59e-3);

    // N65: =D21*((9.08*10^-3)*LOG10(D32)+6.24*10^-3)
    const co2Correction =
      cO2CarbonDioxidePercent *
      (9.08e-3 * Math.log10(safeGasSpecificGravity) + 6.24e-3);

    // N67: =D22*((8.49*10^-3)*LOG10(D32)+3.73*10^-3)
    const h2sCorrection =
      h2SHydrogenSulfidePercent *
      (8.49e-3 * Math.log10(safeGasSpecificGravity) + 3.73e-3);

    // N69: =N61+N63+N65+N67
    const ugCorrected =
      u1Uncorrected + n2Correction + co2Correction + h2sCorrection;

    // N71: =Q63+Q64*D36+Q65*D36^2+Q66*D36^3
    const A =
      coef[0] +
      coef[1] * psuedoReducedPressure +
      coef[2] * psuedoReducedPressure ** 2 +
      coef[3] * psuedoReducedPressure ** 3;

    // N73: =(Q67+Q68*D36+Q69*D36^2+Q70*D36^3)*D35
    const B =
      (coef[4] +
        coef[5] * psuedoReducedPressure +
        coef[6] * psuedoReducedPressure ** 2 +
        coef[7] * psuedoReducedPressure ** 3) *
      psuedoReducedTemperature;

    // N75: =(Q71+Q72*D36+Q73*D36^2+Q74*D36^3)*D35^2
    const C =
      (coef[8] +
        coef[9] * psuedoReducedPressure +
        coef[10] * psuedoReducedPressure ** 2 +
        coef[11] * psuedoReducedPressure ** 3) *
      psuedoReducedTemperature ** 2;

    // N77: =(Q75+Q76*D36+Q77*D36^2+Q78*D36^3)*D35^3
    const D =
      (coef[12] +
        coef[13] * psuedoReducedPressure +
        coef[14] * psuedoReducedPressure ** 2 +
        coef[15] * psuedoReducedPressure ** 3) *
      psuedoReducedTemperature ** 3;

    // N99: =N71+N73+N75+N77
    const F = A + B + C + D;

    // N101: =(N69/D35)*EXP(N79)
    const gasViscosityCP = psuedoReducedTemperature
      ? (ugCorrected / psuedoReducedTemperature) * Math.exp(F)
      : 0;

    return {
      gasViscosityCP: Number.isFinite(gasViscosityCP) ? gasViscosityCP : 0,
      intermediates: {
        gasViscosityCP,
        u1Uncorrected,
        gasSpecificGravity,
        n2Correction,
        co2Correction,
        h2sCorrection,
        ugCorrected,
        A,
        B,
        C,
        D,
        F,
        psuedoReducedTemperature,
        psuedoReducedPressure
      }
    };
  };
